import { v4 as uuid } from "uuid"

import { CommerceService } from "@/services/commerce"

import { getActiveCustomerCart } from "./getCart"

/**
 * Method to create a cart for customer
 *
 * @param {object} customer
 * @param {string|null} cartKey
 * @param {string} currency
 * @param {string|null} country
 * @param {string|null} origin
 * @param {object} user
 * @param {object} options
 * @param {boolean} options.skipActiveCartCheck - Skip checking for active carts (use when cart was just deleted)
 *
 * @return {Promise<object>|null}
 */
export const createCustomerCart = async (customer, cartKey, currency = "EUR", country, origin, user, options = {}) => {
  const { skipActiveCartCheck = false } = options

  if (!customer || !customer.id) throw new Error("Missing customer")
  if (!user || !user.employeeNumber) throw new Error("Missing user")

  const commerceApi = CommerceService.api()
  const employeeNumber = user.employeeNumber

  if (!skipActiveCartCheck) {
    const activeCustomerCarts = await getActiveCustomerCart(customer.id, employeeNumber)

    if (activeCustomerCarts && activeCustomerCarts.length > 0) {
      throw new Error("Customer already has an active cart")
    }
  }

  if (!customer?.defaultBillingAddressId) {
    throw new Error("Missing customer addresses")
  }

  const addresses = Object.fromEntries(customer.addresses.map((address) => [address.id, address]))

  // if there is no shipping address, take default billing address (same as ERP)
  const shippingAddress = addresses[customer.defaultShippingAddressId] || addresses[customer.defaultBillingAddressId]
  const billingAddress = addresses[customer.defaultBillingAddressId]

  cartKey = cartKey || `customer_cart_${customer.customerNumber + "_"}${uuid()}`

  let cartStore = null
  // apply default cart store for cart
  if (customer?.stores?.length > 0) {
    const defaultStore = customer.stores.find((store) => store?.key === "cart-store")
    const firstStore = customer.stores[0]

    // if customer does not have a cart store assigned to him, but still has stores, use the first store
    cartStore = defaultStore || firstStore
  }

  // custom type created for custom line custom fields
  const customTypeCart = (await commerceApi.types().withKey({ key: "customCartTypeKey" }).get().execute())?.body

  if (!customTypeCart) {
    throw new Error("Custom type not found")
  }

  const payload = {
    key: cartKey,
    currency,
    customerId: customer.id,
    customerEmail: customer.email,
    ...(cartStore && {
      store: {
        typeId: "store",
        key: cartStore.key,
      },
    }),
    country,
    origin,
    shippingAddress,
    billingAddress,
    custom: {
      type: { id: customTypeCart.id, typeId: "type" },
      fields: {
        createdByEmployeeNumber: employeeNumber,
      },
    },
  }

  try {
    const cart = (
      await commerceApi
        .carts()
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    ).body

    return cart
  } catch (err) {
    console.warn("Error creating cart", err)
    return null
  }
}
